import React, { FC, memo, useCallback, useEffect, useState } from 'react'

import { useSetFocus } from '@please/lrud'

import { Button } from '@/components/Button/Button'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { scrollOptions } from '@/constants/scrollers'
import { ScrollContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { Keys } from '@/platform/platform'
import {
  SelectOptionType,
  SelectPopupProps,
} from '@/types/componentsPropsTypes'

import { SelectOption } from './selectOption'
import {
  Description,
  SelectPopupBox,
  SelectPopupList,
  SelectPopupElem,
  SelectPopupHeader,
  SelectPopupFooter,
  Title,
} from './styles'

const SelectPopupRaw: FC<SelectPopupProps> = ({
  title,
  description,
  selectOptionList,
  activeOptionId,
  prevFocusId,
  onSelect,
  onCancel,
  closeSelectPopup,
  setActiveOptionId,
}) => {
  const setFocus = useSetFocus()
  const { t } = useCommonTranslation()
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(
    activeOptionId,
  )
  const focusId = 'SelectList'
  const defaultOption = selectOptionList.findIndex(
    (el) => el.id === activeOptionId,
  )
  const startFocusChild = defaultOption > 0 ? defaultOption : 0

  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
    scrollOptions: scrollOptions.SelectListScroll,
    startFocusChild,
  })

  useEffect(() => {
    setFocus(focusIds.POPUP)
  }, [])

  const saveHandler = useCallback(() => {
    onSelect && selectedOptionId && onSelect(selectedOptionId)
    selectedOptionId && setActiveOptionId(selectedOptionId)
    setFocus(prevFocusId)
    closeSelectPopup()
  }, [selectedOptionId, prevFocusId, onSelect, closeSelectPopup])

  const cancelHandler = useCallback(() => {
    onCancel && onCancel()
    setFocus(prevFocusId)
    closeSelectPopup()
  }, [prevFocusId, onCancel, closeSelectPopup])

  const keyPressHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode == Keys.BACKSPACE || e.keyCode == Keys.BACK) {
        e.preventDefault()
        e.stopPropagation()
        cancelHandler()
      }
    },
    [closeSelectPopup],
  )

  useEffect(() => {
    window.addEventListener('keyup', keyPressHandler)
    return () => {
      window.removeEventListener('keyup', keyPressHandler)
    }
  }, [keyPressHandler])

  const onSelectedHandler = (selectedOptionId: string) => {
    setSelectedOptionId(selectedOptionId)
    setFocus(`${focusIds.BUTTON}${t('save')}`)
  }

  return (
    <SelectPopupBox
      focusId={focusIds.POPUP}
      isTrap={true}
      orientation={orientation.VERTICAL}
    >
      <SelectPopupElem>
        <SelectPopupHeader>
          {title && <Title>{title}</Title>}
          {description && <Description>{description}</Description>}
        </SelectPopupHeader>
        <SelectPopupList
          focusId={focusId}
          orientation={orientation.VERTICAL}
          defaultFocusChild={defaultFocusChild}
          onMove={onMove}
        >
          <ScrollContainer
            id={scrollOptions.SelectListScroll.containerId}
            height={scrollOptions.SelectListScroll.height}
          >
            {selectOptionList.length &&
              selectOptionList.map((selectOption: SelectOptionType) => {
                return (
                  <SelectOption
                    key={selectOption.id}
                    selectOption={selectOption}
                    isActive={selectOption.id === selectedOptionId}
                    onSelected={() => onSelectedHandler(selectOption.id)}
                  />
                )
              })}
          </ScrollContainer>
        </SelectPopupList>
        <SelectPopupFooter
          orientation={orientation.HORIZONTAL}
          defaultFocusChild={1}
        >
          <Button
            iconSrc={icons.navigation.exit}
            text={t('cancel')}
            onClick={cancelHandler}
          />
          <Button
            iconSrc={icons.navigation.arrowRight}
            text={t('save')}
            onClick={saveHandler}
          />
        </SelectPopupFooter>
      </SelectPopupElem>
    </SelectPopupBox>
  )
}

export const SelectPopup = memo(SelectPopupRaw)
